import { GAME_NAV_POTION_IDS } from "../constants/potions";

export const POTION_SHORTCUTS_PREFERENCE_KEY = "vocabularium_potion_shortcuts";
export const POTION_SHORTCUTS_CHANGE_EVENT = "potionShortcutsChanged";

export const POTION_SHORTCUTS_MODES = {
  ENABLED: "enabled",
  DISABLED: "disabled",
};

export const POTION_SHORTCUTS_MODE_OPTIONS = [
  {
    value: POTION_SHORTCUTS_MODES.ENABLED,
    label: "Utiliser les raccourcis clavier",
  },
  {
    value: POTION_SHORTCUTS_MODES.DISABLED,
    label: "Ne pas utiliser les raccourcis clavier",
  },
];

const DIGIT_KEYS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
const FUNCTION_KEYS = ["F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9", "F10"];
const VALID_SHORTCUT_KEYS = new Set([...DIGIT_KEYS, ...FUNCTION_KEYS]);
const FUNCTION_KEY_PATTERN = /^f(?:10|[1-9])$/i;

function normalizeShortcutKey(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (VALID_SHORTCUT_KEYS.has(trimmed)) return trimmed;
  if (FUNCTION_KEY_PATTERN.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  return null;
}

function dispatchChange() {
  window.dispatchEvent(new CustomEvent(POTION_SHORTCUTS_CHANGE_EVENT));
}

function readRawPreference() {
  try {
    const raw = localStorage.getItem(POTION_SHORTCUTS_PREFERENCE_KEY);
    if (!raw) return { mode: POTION_SHORTCUTS_MODES.DISABLED, bindings: {} };
    const parsed = JSON.parse(raw);
    const mode =
      parsed?.mode === POTION_SHORTCUTS_MODES.ENABLED
        ? POTION_SHORTCUTS_MODES.ENABLED
        : POTION_SHORTCUTS_MODES.DISABLED;
    const bindings =
      parsed?.bindings && typeof parsed.bindings === "object" ? parsed.bindings : {};
    const sanitizedBindings = {};
    for (const potionId of GAME_NAV_POTION_IDS) {
      const value = bindings[potionId];
      const normalized = normalizeShortcutKey(value);
      if (normalized) {
        sanitizedBindings[potionId] = normalized;
      }
    }
    return { mode, bindings: sanitizedBindings };
  } catch {
    return { mode: POTION_SHORTCUTS_MODES.DISABLED, bindings: {} };
  }
}

function writeRawPreference(next) {
  try {
    localStorage.setItem(POTION_SHORTCUTS_PREFERENCE_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
  dispatchChange();
}

export function readPotionShortcutsMode() {
  return readRawPreference().mode;
}

export function arePotionShortcutsEnabled() {
  return readPotionShortcutsMode() === POTION_SHORTCUTS_MODES.ENABLED;
}

export function writePotionShortcutsMode(mode) {
  const current = readRawPreference();
  const nextMode =
    mode === POTION_SHORTCUTS_MODES.ENABLED
      ? POTION_SHORTCUTS_MODES.ENABLED
      : POTION_SHORTCUTS_MODES.DISABLED;
  writeRawPreference({ ...current, mode: nextMode });
}

export function readPotionShortcutBindings() {
  return readRawPreference().bindings;
}

export function writePotionShortcutBindings(bindings) {
  const current = readRawPreference();
  writeRawPreference({ ...current, bindings });
}

export function subscribePotionShortcutsPreference(onChange) {
  const handler = () => onChange(readRawPreference());
  window.addEventListener(POTION_SHORTCUTS_CHANGE_EVENT, handler);
  return () => window.removeEventListener(POTION_SHORTCUTS_CHANGE_EVENT, handler);
}

/** Chiffre 0–9 (rangée du haut ou pavé numérique) ou touche F1–F10, ou null. */
export function parsePotionShortcutDigit(event) {
  const { key, code } = event;
  if (typeof key === "string") {
    const normalized = normalizeShortcutKey(key);
    if (normalized) return normalized;
  }
  const numpadMatch = /^Numpad([0-9])$/.exec(code || "");
  if (numpadMatch) return numpadMatch[1];
  const functionKeyMatch = /^F(10|[1-9])$/i.exec(code || "");
  if (functionKeyMatch) return `F${functionKeyMatch[1]}`;
  return null;
}

export function formatShortcutKeyFromEvent(event) {
  return parsePotionShortcutDigit(event);
}

export function getPotionIdForShortcutEvent(event) {
  if (!arePotionShortcutsEnabled()) return null;

  const digit = parsePotionShortcutDigit(event);
  if (!digit) return null;

  const bindings = readPotionShortcutBindings();
  for (const potionId of GAME_NAV_POTION_IDS) {
    if (bindings[potionId] === digit) {
      return potionId;
    }
  }

  return null;
}

export function assignPotionShortcutKey(potionId, keyLabel) {
  const current = readRawPreference();
  const nextBindings = { ...current.bindings };

  if (!keyLabel) {
    delete nextBindings[potionId];
  } else {
    const normalized = normalizeShortcutKey(keyLabel);
    if (!normalized) {
      return nextBindings;
    }
    for (const [id, assigned] of Object.entries(nextBindings)) {
      if (id !== potionId && assigned === normalized) {
        delete nextBindings[id];
      }
    }
    nextBindings[potionId] = normalized;
  }

  writeRawPreference({ ...current, bindings: nextBindings });
  return nextBindings;
}
